import { useListItem } from "@floating-ui/react";
import { PolymorphicComponentProps } from "@omni/types/components/utils/polymorphic.types";
import { cn } from "@omni/utils/styles";
import React from "react";

import { useDropdownContext } from "@/components/General/Dropdown/context/dropdown.context";

type ItemProps<T extends React.ElementType> = PolymorphicComponentProps<
    T,
    {
        label: string | number;
        onClick?: () => void;
        disabled?: boolean;
    },
    "a" | "button"
>;

export const Item = <T extends React.ElementType = "button">({
    label,
    onClick,
    className,
    disabled,
    tag,
    children,
    ...attrs
}: ItemProps<T>) => {
    const Tag = tag || "button";
    const { activeIndex, selectedIndex, getItemProps, onSelect } =
        useDropdownContext();

    const { ref, index } = useListItem({ label: label.toString() });

    const isActive = activeIndex === index;
    const isSelected = selectedIndex === index;

    const handleOnClick = () => {
        onSelect(index);

        if (onClick) {
            setTimeout(() => onClick());
        }
    };

    return (
        <Tag
            {...attrs}
            {...getItemProps()}
            ref={ref}
            role="option"
            aria-selected={isActive && isSelected}
            tabIndex={isActive ? 0 : -1}
            disabled={disabled}
            onClick={handleOnClick}
            className={cn(
                "tw:flex tw:w-full tw:py-2 tw:px-4 tw:bg-background tw:hover:bg-grey! tw:rounded-md tw:text-black!",
                {
                    "tw:bg-grey-input": isActive && isSelected,
                    "tw:text-muted-foreground! tw:hover:bg-white! tw:hover:no-underline!":
                        disabled
                },
                className
            )}
        >
            {children || label}
        </Tag>
    );
};
